import { useState, useMemo } from 'react'
import { useExecutions } from '../../context/ExecutionContext'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { exportToExcel } from '../../utils/exportExcel'
import { MobileCard, MobileOnly, DesktopOnly } from '../../components/MobileCard'

const orderStatusStyles: Record<string, { color: string; bg: string; border: string }> = {
  filled: { color: '#22c55e', bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.18)' },
  placed: { color: '#eab308', bg: 'rgba(234,179,8,0.1)', border: 'rgba(234,179,8,0.18)' },
  rejected: { color: '#ef4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.12)' },
  cancelled: { color: '#94a3b8', bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.06)' },
}

export function TraderOrderBook() {
  const { orders, executions } = useExecutions()
  const { session } = useAuth()
  const { addToast } = useToast()
  const traderId = session?.user.id || 'trader-1'
  const traderUsername = session?.user.username || 'trader1'

  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [stratFilter, setStratFilter] = useState<string>('all')

  const myExecs = useMemo(() =>
    executions.filter((e) => e.traderId === traderId || e.traderId === traderUsername || e.traderId === 'trader1'),
    [executions, traderId, traderUsername]
  )

  // Today only — filter to current day
  const today = new Date().toISOString().split('T')[0]

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (!myExecs.some(e => e.id === o.executionId)) return false
      const d = o.timestamp.split('T')[0]
      if (d !== today) return false
      if (statusFilter !== 'all' && o.status !== statusFilter) return false
      if (stratFilter !== 'all' && o.strategyType !== stratFilter) return false
      return true
    })
  }, [orders, myExecs, today, statusFilter, stratFilter])

  const summary = useMemo(() => ({
    total: filteredOrders.length,
    filled: filteredOrders.filter(o => o.status === 'filled').length,
    rejected: filteredOrders.filter(o => o.status === 'rejected').length,
    cancelled: filteredOrders.filter(o => o.status === 'cancelled').length,
  }), [filteredOrders])

  return (
    <div>
      <div className="page-head">
        <h1>Orders & Trades</h1>
        <p className="page-sub">Today's orders — {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}</p>
        <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <select className="cfg-input" style={{ width: 'auto', padding: '0.25rem 0.4rem', fontSize: '0.75rem' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="filled">Filled</option>
            <option value="placed">Placed</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select className="cfg-input" style={{ width: 'auto', padding: '0.25rem 0.4rem', fontSize: '0.75rem' }} value={stratFilter} onChange={(e) => setStratFilter(e.target.value)}>
            <option value="all">All Strategies</option>
            <option value="rsi_straddle">RSI Straddle</option>
            <option value="trending_expiry">Trending Expiry</option>
          </select>
          <button className="btn btn-sm btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
            onClick={() => {
              exportToExcel(filteredOrders.map(o => {
                const exec = myExecs.find(e => e.id === o.executionId)
                return {
                  Time: new Date(o.timestamp).toLocaleString(), OrderID: o.orderId || '', Set: exec?.setName || '',
                  Underlying: o.underlying, Strike: o.strike, Type: o.optionType, Side: o.side,
                  OrderType: o.orderType, Qty: o.quantity, Price: o.price, Status: o.status,
                  FilledQty: o.filledQty ?? '', FilledPrice: o.filledPrice ?? '', Reason: o.reason || '',
                }
              }), `LaMoney_OrderBook_${today}`)
              addToast('Excel exported successfully', 'success')
            }}>
            ⬇ Export
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="dash-mtm-strip" style={{ marginBottom: '1.25rem' }}>
        <div className="mtm-item">
          <span className="mtm-label">Total Orders</span>
          <span className="mtm-value">{summary.total}</span>
        </div>
        <div className="mtm-item">
          <span className="mtm-label">Filled</span>
          <span className="mtm-value" style={{ color: 'var(--green)' }}>{summary.filled}</span>
        </div>
        <div className="mtm-item">
          <span className="mtm-label">Rejected</span>
          <span className="mtm-value" style={{ color: 'var(--red)' }}>{summary.rejected}</span>
        </div>
        <div className="mtm-item">
          <span className="mtm-label">Cancelled</span>
          <span className="mtm-value" style={{ color: 'var(--text-muted)' }}>{summary.cancelled}</span>
        </div>
      </div>

      {/* Orders — Desktop Table */}
      <DesktopOnly>
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Order ID</th>
                  <th>Set</th>
                  <th>Underlying</th>
                  <th>Strike</th>
                  <th>Type</th>
                  <th>Side</th>
                  <th>Order Type</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Filled Qty</th>
                  <th>Filled Price</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 && (
                  <tr><td colSpan={14} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No orders for today. Orders will appear here once strategies start executing.</td></tr>
                )}
                {filteredOrders.map((o) => {
                  const exec = myExecs.find(e => e.id === o.executionId)
                  const st = orderStatusStyles[o.status] || { color: '#94a3b8', bg: 'transparent', border: 'transparent' }
                  return (
                    <tr key={o.id}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{new Date(o.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                      <td style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.6875rem', color: 'var(--text-secondary)' }}>{o.orderId || '—'}</td>
                      <td style={{ fontWeight: 600 }}>{exec?.setName || '—'}</td>
                      <td>{o.underlying}</td>
                      <td style={{ fontFamily: 'var(--font-mono, monospace)' }}>{o.strike}</td>
                      <td><span style={{ color: o.optionType === 'CE' ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>{o.optionType}</span></td>
                      <td><span style={{ color: o.side === 'SELL' ? 'var(--red)' : 'var(--green)', fontWeight: 600 }}>{o.side}</span></td>
                      <td>{o.orderType}</td>
                      <td style={{ fontFamily: 'var(--font-mono, monospace)' }}>{o.quantity}</td>
                      <td style={{ fontFamily: 'var(--font-mono, monospace)' }}>₹{o.price.toLocaleString()}</td>
                      <td>
                        <span style={{
                          color: st.color, fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase',
                          padding: '1px 8px', borderRadius: '999px', background: st.bg, border: `1px solid ${st.border}`,
                        }}>{o.status}</span>
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono, monospace)' }}>{o.filledQty ?? '—'}</td>
                      <td style={{ fontFamily: 'var(--font-mono, monospace)' }}>{o.filledPrice ? `₹${o.filledPrice.toLocaleString()}` : '—'}</td>
                      <td style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{o.reason || '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </DesktopOnly>

      {/* Orders — Mobile Card View */}
      <MobileOnly>
        {filteredOrders.length === 0 ? (
          <div className="m-empty">
            <div className="m-empty-icon">📋</div>
            No orders yet today
          </div>
        ) : (
          <div className="m-card-list">
            {filteredOrders.map((o) => {
              const exec = myExecs.find(e => e.id === o.executionId)
              const statusVariant = o.status === 'filled' ? 'green' as const
                : o.status === 'rejected' ? 'red' as const
                : o.status === 'placed' ? 'yellow' as const : 'muted' as const
              return (
                <MobileCard
                  key={o.id}
                  title={`${o.underlying} ${o.strike}`}
                  subtitle={`${exec?.setName || '—'} · ${new Date(o.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`}
                  status={{ label: o.status, variant: statusVariant }}
                  fields={[
                    { label: 'Side', value: o.side, accent: o.side === 'SELL' ? 'red' : 'green', bold: true },
                    { label: 'Type', value: o.optionType, accent: o.optionType === 'CE' ? 'green' : 'red' },
                    { label: 'Qty', value: String(o.quantity) },
                    { label: 'Price', value: `₹${o.price.toLocaleString()}`, bold: true },
                  ]}
                />
              )
            })}
          </div>
        )}
      </MobileOnly>
    </div>
  )
}
